import React, { useEffect, useMemo, useState } from "react";
import axios from "../../import/axiosConfig";
import Swal from "sweetalert2";
import { PurchaseProductList_Product } from "../../import/ApiEndPoint/Api";

const API = {
  create: "/target/yearly-target/create",
  list: "/target/yearly-target/list",
  view: "/target/yearly-target/view",
  update: "/target/yearly-target/update",
  updateMonth: "/target/yearly-target/update-month",
  delete: "/target/yearly-target/delete",
};

const FILTER_ALL = "__ALL__";
const LS_PREFIX = "YEARLY_TARGET_PAGE_V4";

const FY_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const CALENDAR_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (v) => str(v).trim().toLowerCase();

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const r2 = (v) => Number(num(v).toFixed(2));
const money = (v) => `₹ ${Number(num(v)).toFixed(2)}`;
const getUser = () => JSON.parse(localStorage.getItem("userData") || "{}");

const first = (...values) => {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const safeReadLS = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const safeWriteLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // localStorage can be full or blocked; DB save still works.
  }
};

const safeRemoveLS = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // ignore
  }
};

const productsCacheKey = (db) => `${LS_PREFIX}:products:${db || "NO_DB"}`;
const targetsCacheKey = (db) => `${LS_PREFIX}:targets:${db || "NO_DB"}`;

const formDraftKey = (db, fy, editId = "new") =>
  `${LS_PREFIX}:draft:${db || "NO_DB"}:${fy || "NO_FY"}:${editId || "new"}`;

const monthDraftKey = (db, fy, month) =>
  `${LS_PREFIX}:month-draft:${db || "NO_DB"}:${fy || "NO_FY"}:${month}`;

const expandKey = (db, fy, mode) =>
  `${LS_PREFIX}:expanded:${db || "NO_DB"}:${fy || "NO_FY"}:${mode}`;

const cleanName = (v) => {
  if (!v) return "";

  if (typeof v === "object") {
    return str(
      v.categoryName ||
        v.subCategoryName ||
        v.subcategoryName ||
        v.SubCategoryName ||
        v.groupName ||
        v.name ||
        v.title ||
        v.label ||
        v.value ||
        v.Product_Category ||
        v.Product_SubCategory ||
        "",
    ).trim();
  }

  return str(v).trim();
};

const getCurrentFY = () => {
  const d = new Date();
  const y = d.getFullYear();

  return d.getMonth() >= 3
    ? `${y}-${String((y + 1) % 100).padStart(2, "0")}`
    : `${y - 1}-${String(y % 100).padStart(2, "0")}`;
};

const getFYStartYear = (fyLabel) => {
  const n = Number(String(fyLabel || "").split("-")[0]);
  return Number.isFinite(n) ? n : 0;
};

const getCurrentFYMonthIndex = () => {
  const currentMonthName = CALENDAR_MONTHS[new Date().getMonth()];
  return FY_MONTHS.indexOf(currentMonthName);
};

const isMonthCompleted = (fyLabel, monthName) => {
  const targetFyStart = getFYStartYear(fyLabel);
  const currentFyStart = getFYStartYear(getCurrentFY());
  const monthIndex = FY_MONTHS.indexOf(str(monthName).trim());
  const currentMonthIndex = getCurrentFYMonthIndex();

  if (!targetFyStart || monthIndex < 0) return true;
  if (targetFyStart < currentFyStart) return true;
  if (targetFyStart > currentFyStart) return false;

  return monthIndex < currentMonthIndex;
};

const getFYOptions = () => {
  const start = Number(getCurrentFY().split("-")[0]);
  const list = [];

  for (let y = start - 3; y <= start + 3; y += 1) {
    list.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }

  return list.reverse();
};

const getProductId = (p = {}) =>
  str(p._id || p.id || p.productId || p.ProductId || p.sId || "");

const getProductName = (p = {}) =>
  str(
    p.Product_Title ||
      p.productName ||
      p.ProductName ||
      p.name ||
      p.title ||
      "Unnamed Product",
  );

const getProductCategory = (p = {}) =>
  cleanName(
    first(
      p.category,
      p.Category,
      p.catageory,
      p.Catageory,
      p.productCategory,
      p.Product_Category,
      p.categoryName,
      p.groupName,
    ),
  ) || "Uncategorized";

const getProductSubCategory = (p = {}) =>
  cleanName(
    first(
      p.subcategory,
      p.subCategory,
      p.SubCategory,
      p.sub_category,
      p.productSubCategory,
      p.Product_SubCategory,
      p.subCategoryName,
      p.subcategoryName,
    ),
  ) || "No Sub Category";

const getProductColor = (p = {}) =>
  cleanName(
    first(
      p.color,
      p.Color,
      p.colour,
      p.Colour,
      p.productColor,
      p.Product_Color,
      p.shade,
      p.Shade,
      p.variantColor,
      p.VariantColor,
    ),
  ) || "No Color";

const getProductProfitColorCode = (p = {}) =>
  str(first(p.profitColorCode, p.ProfitColorCode)).trim();

const getProductRate = (p = {}) =>
  num(
    first(
      p.SalesRate,
      p.salesRate,
      p.SaleRate,
      p.saleRate,
      p.BasicPrice,
      p.basicPrice,
      p.price,
      p.Price,
      p.rate,
      p.Rate,
      p.Product_MRP,
    ),
  );

const getSecondarySize = (p = {}) => {
  const v = num(
    first(
      p.secondarySize,
      p.secondarysize,
      p.SecondarySize,
      p.secondaryQty,
      p.SecondaryQty,
      p.secondaryUnitSize,
      p.SecondaryUnitSize,
      p.conversion,
      p.Conversion,
    ),
  );

  return v > 0 ? v : 1;
};

const getSecondaryUnitName = (p = {}) =>
  str(
    first(
      p.secondaryUnit,
      p.SecondaryUnit,
      p.secondaryUnitName,
      p.SecondaryUnitName,
      p.secUnit,
      p.unitSecondary,
    ),
  ) || "Secondary";

const getPrimaryUnitName = (p = {}) =>
  str(
    first(
      p.primaryUnit,
      p.PrimaryUnit,
      p.primaryUnitName,
      p.PrimaryUnitName,
      p.unit,
      p.Unit,
    ),
  ) || "Qty";

const makeOptionList = (rows, getter) => {
  const map = new Map();

  arr(rows).forEach((row) => {
    const label = getter(row);
    const key = norm(label);
    if (key && !map.has(key)) map.set(key, label);
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a).localeCompare(String(b)),
  );
};

const getSavedProducts = (target = {}) =>
  arr(target.productTargets?.length ? target.productTargets : target.products);

const getMonthProducts = (monthRow = {}) =>
  arr(
    monthRow.productTargets?.length
      ? monthRow.productTargets
      : monthRow.products,
  );

const getSavedProductId = (product = {}) =>
  str(
    first(
      product.productId,
      product.ProductId,
      product._id,
      product.id,
      product.sId,
      product.productCode,
      product.Product_Code,
      product.productName,
    ),
  );

const makeFallbackMonth = (target, month, index) => {
  const products = getSavedProducts(target).map((row) => {
    const secondaryQty = r2(num(row.secondaryQty || row.sQty) / 12);
    const primaryQty = r2(num(row.primaryQty || row.pQty || row.qty) / 12);
    const total = r2(num(row.total || row.totalPrice || row.targetAmount) / 12);

    return {
      ...row,
      secondaryQty,
      sQty: secondaryQty,
      primaryQty,
      pQty: primaryQty,
      qty: primaryQty,
      qtyAssign: primaryQty,
      total,
      totalPrice: total,
      targetAmount: total,
    };
  });

  return {
    month,
    monthIndex: index,
    targetTotal: r2(num(target.grandTotal) / 12),
    productTargets: products,
    products,
    isEdited: false,
  };
};

const getMonthlyTargets = (target = {}) => {
  const rows = arr(target.monthlyTargets);

  return FY_MONTHS.map((month, index) => {
    const found = rows.find((row) => norm(row.month) === norm(month));
    if (!found) return makeFallbackMonth(target, month, index);

    const products = arr(
      found.productTargets?.length ? found.productTargets : found.products,
    );

    return {
      ...found,
      month,
      monthIndex: index,
      targetTotal: num(found.targetTotal),
      productTargets: products,
      products,
    };
  });
};

const aggregateProductsFromMonths = (monthlyTargets = []) => {
  const map = new Map();

  arr(monthlyTargets).forEach((monthRow) => {
    getMonthProducts(monthRow).forEach((product) => {
      const key =
        getSavedProductId(product) || str(product.productName || product.name);

      if (!key) return;

      if (!map.has(key)) {
        map.set(key, {
          ...product,
          productId: str(
            first(product.productId, product._id, product.id, key),
          ),
          secondaryQty: 0,
          sQty: 0,
          primaryQty: 0,
          pQty: 0,
          qty: 0,
          qtyAssign: 0,
          total: 0,
          totalPrice: 0,
          targetAmount: 0,
        });
      }

      const row = map.get(key);

      row.secondaryQty += num(first(product.secondaryQty, product.sQty));
      row.sQty = row.secondaryQty;

      row.primaryQty += num(
        first(product.primaryQty, product.pQty, product.qty, product.qtyAssign),
      );
      row.pQty = row.primaryQty;
      row.qty = row.primaryQty;
      row.qtyAssign = row.primaryQty;

      row.total += num(
        first(product.total, product.totalPrice, product.targetAmount),
      );
      row.totalPrice = row.total;
      row.targetAmount = row.total;
    });
  });

  return Array.from(map.values()).map((product) => ({
    ...product,
    secondaryQty: r2(product.secondaryQty),
    sQty: r2(product.sQty),
    primaryQty: r2(product.primaryQty),
    pQty: r2(product.pQty),
    qty: r2(product.qty),
    qtyAssign: r2(product.qtyAssign),
    total: r2(product.total),
    totalPrice: r2(product.totalPrice),
    targetAmount: r2(product.targetAmount),
  }));
};

const productToRow = (product = {}, qtyMap = {}) => {
  const id = getProductId(product);
  const secondaryQty = num(qtyMap[id]);
  const secondarySize = getSecondarySize(product);
  const primaryQty = r2(secondaryQty * secondarySize);
  const saleRate = getProductRate(product);
  const total = r2(primaryQty * saleRate);

  return {
    id,
    product,
    productName: getProductName(product),
    category: getProductCategory(product),
    subCategory: getProductSubCategory(product),
    color: getProductColor(product),
    profitColorCode: getProductProfitColorCode(product),
    saleRate,
    secondaryQty,
    secondarySize,
    primaryQty,
    total,
    primaryUnitName: getPrimaryUnitName(product),
    secondaryUnitName: getSecondaryUnitName(product),
  };
};

const savedProductToProductMasterShape = (product = {}) => {
  const id = str(product.productId || product._id || product.id || "");

  return {
    _id: id,
    id,
    productId: id,

    Product_Title:
      product.productName ||
      product.Product_Title ||
      product.name ||
      "Unnamed Product",

    productName:
      product.productName ||
      product.Product_Title ||
      product.name ||
      "Unnamed Product",

    category: product.category || product.catageory || "Uncategorized",
    catageory: product.category || product.catageory || "Uncategorized",

    subCategory:
      product.subCategory || product.SubCategory || "No Sub Category",

    color: product.color || "No Color",
    shade: product.color || "No Color",
    profitColorCode: product.profitColorCode || product.ProfitColorCode || "",

    saleRate: product.saleRate || product.basicPrice || product.price || 0,
    basicPrice: product.saleRate || product.basicPrice || product.price || 0,
    price: product.saleRate || product.basicPrice || product.price || 0,

    secondarySize: product.secondarySize || 1,
    secondaryUnitName: product.secondaryUnitName || "Secondary",
    primaryUnitName: product.primaryUnitName || "Qty",
  };
};

const makeProductPayload = (sourceRows) =>
  sourceRows.map((row) => ({
    productId: row.id,
    productName: row.productName,
    category: row.category,
    catageory: row.category,
    subCategory: row.subCategory,
    color: row.color,
    profitColorCode: row.profitColorCode || row.product?.profitColorCode || "",

    basicPrice: Number(row.saleRate || 0),
    saleRate: Number(row.saleRate || 0),
    price: Number(row.saleRate || 0),

    secondaryUnitName: row.secondaryUnitName,
    primaryUnitName: row.primaryUnitName,
    secondarySize: Number(row.secondarySize || 1),

    secondaryQty: Number(row.secondaryQty || 0),
    sQty: Number(row.secondaryQty || 0),

    primaryQty: Number(row.primaryQty || 0),
    pQty: Number(row.primaryQty || 0),
    qty: Number(row.primaryQty || 0),
    qtyAssign: Number(row.primaryQty || 0),

    total: Number(row.total || 0),
    totalPrice: Number(row.total || 0),
    targetAmount: Number(row.total || 0),
  }));

const makeGroups = (rowList = []) => {
  const catMap = new Map();

  arr(rowList).forEach((row) => {
    const catLabel = row.category || "Uncategorized";
    const catKey = norm(catLabel) || "uncategorized";
    const subLabel = row.subCategory || "No Sub Category";
    const subKey = norm(subLabel) || "no-sub-category";

    if (!catMap.has(catKey)) {
      catMap.set(catKey, {
        key: catKey,
        label: catLabel,
        total: 0,
        qty: 0,
        count: 0,
        subMap: new Map(),
      });
    }

    const cat = catMap.get(catKey);

    if (!cat.subMap.has(subKey)) {
      cat.subMap.set(subKey, {
        key: subKey,
        label: subLabel,
        total: 0,
        qty: 0,
        count: 0,
        rows: [],
      });
    }

    const sub = cat.subMap.get(subKey);

    sub.rows.push(row);
    sub.total += num(row.total);
    sub.qty += num(row.primaryQty);
    sub.count += 1;

    cat.total += num(row.total);
    cat.qty += num(row.primaryQty);
    cat.count += 1;
  });

  return Array.from(catMap.values())
    .map((cat) => ({
      ...cat,
      subGroups: Array.from(cat.subMap.values()).sort((a, b) =>
        String(a.label).localeCompare(String(b.label)),
      ),
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

const upsertTarget = (list, target) => {
  if (!target) return arr(list);

  const targetId = str(target._id);
  const targetFY = str(target.fyear);
  let found = false;

  const next = arr(list).map((item) => {
    const sameId = targetId && str(item._id) === targetId;
    const sameFY = targetFY && str(item.fyear) === targetFY;

    if (sameId || sameFY) {
      found = true;
      return { ...item, ...target };
    }

    return item;
  });

  if (!found) next.unshift(target);
  return next;
};

const injectOnce = () => {
  const old = document.getElementById("yearly-target-local-grouped-css");
  if (old) old.remove();

  const style = document.createElement("style");
  style.id = "yearly-target-local-grouped-css";

  style.textContent = `
    .yt-page{
      height:100vh;
      width:100%;
      overflow:hidden;
      background:#f8fafc;
      color:#0f172a;
      display:flex;
      flex-direction:column;
      padding:0;
      margin:0;
      font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
    }

    .yt-top{
      min-height:42px;
      padding:5px 7px;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:7px;
      background:linear-gradient(135deg,#0f172a,#1d4ed8);
      color:#fff;
      flex:0 0 auto;
    }

    .yt-title{
      display:flex;
      flex-direction:column;
      gap:1px;
      min-width:130px;
    }

    .yt-title h2{
      margin:0;
      font-size:15px;
      line-height:1;
      font-weight:950;
      letter-spacing:.01em;
      white-space:nowrap;
    }

    .yt-title span{
      font-size:9px;
      color:rgba(255,255,255,.72);
      font-weight:800;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
      max-width:760px;
    }

    .yt-actions{
      display:flex;
      align-items:center;
      gap:5px;
      flex-wrap:wrap;
      justify-content:flex-end;
    }

    .yt-btn{
      height:30px;
      border:none;
      border-radius:8px;
      padding:0 9px;
      font-size:11px;
      font-weight:950;
      cursor:pointer;
      white-space:nowrap;
      transition:.12s ease;
    }

    .yt-btn:hover{
      transform:translateY(-1px);
    }

    .yt-btn.primary{
      background:#16a34a;
      color:#fff;
    }

    .yt-btn.light{
      background:#fff;
      color:#0f172a;
    }

    .yt-btn.dark{
      background:#0f172a;
      color:#fff;
      border:1px solid rgba(255,255,255,.16);
    }

    .yt-btn.danger{
      background:#fee2e2;
      color:#b91c1c;
    }

    .yt-btn.warn{
      background:#fff7ed;
      color:#c2410c;
      border:1px solid #fed7aa;
    }

    .yt-btn.blue{
      background:#2563eb;
      color:#fff;
    }

    .yt-btn.soft{
      background:#eff6ff;
      color:#1d4ed8;
      border:1px solid #bfdbfe;
    }

    .yt-btn:disabled{
      opacity:.55;
      cursor:not-allowed;
      transform:none;
    }

    .yt-filter{
      display:grid;
      grid-template-columns:100px 150px 150px 130px minmax(160px,1fr) 150px 150px;
      gap:5px;
      align-items:end;
      padding:5px 7px;
      background:#fff;
      border-bottom:1px solid #dbe3ef;
      flex:0 0 auto;
    }

    .yt-field{
      display:flex;
      flex-direction:column;
      gap:2px;
      min-width:0;
    }

    .yt-label{
      font-size:9px;
      font-weight:950;
      color:#64748b;
      text-transform:uppercase;
      letter-spacing:.04em;
      line-height:1;
    }

    .yt-input,
    .yt-select,
    .yt-stat{
      height:29px;
      border:1px solid #cbd5e1;
      border-radius:8px;
      padding:0 8px;
      background:#fff;
      color:#0f172a;
      outline:none;
      font-size:12px;
      font-weight:850;
      min-width:0;
    }

    .yt-input:focus,
    .yt-select:focus{
      border-color:#2563eb;
      box-shadow:0 0 0 2px rgba(37,99,235,.12);
    }

    .yt-stat{
      display:flex;
      align-items:center;
      justify-content:space-between;
      background:#ecfdf5;
      border-color:#bbf7d0;
      color:#047857;
      font-weight:950;
      font-variant-numeric:tabular-nums;
      white-space:nowrap;
    }

    .yt-flowbar{
      height:34px;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:8px;
      padding:4px 7px;
      background:#f1f5f9;
      border-bottom:1px solid #dbe3ef;
      flex:0 0 auto;
    }

    .yt-step-wrap{
      display:flex;
      align-items:center;
      gap:5px;
      min-width:0;
    }

    .yt-step{
      height:24px;
      display:inline-flex;
      align-items:center;
      gap:5px;
      border-radius:999px;
      padding:0 9px;
      font-size:10px;
      font-weight:950;
      background:#fff;
      border:1px solid #dbe3ef;
      color:#475569;
      white-space:nowrap;
    }

    .yt-step.active{
      background:#eff6ff;
      border-color:#bfdbfe;
      color:#1d4ed8;
    }

    .yt-step.done{
      background:#ecfdf5;
      border-color:#bbf7d0;
      color:#047857;
    }

    .yt-status-note{
      font-size:11px;
      font-weight:900;
      color:#475569;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    }

    .yt-main{
      flex:1 1 auto;
      min-height:0;
      display:flex;
      overflow:hidden;
      background:#fff;
    }

    .yt-create-area,
    .yt-month-area{
      flex:1 1 auto;
      min-width:0;
      min-height:0;
      overflow:auto;
      background:#fff;
    }

    .yt-table{
      width:100%;
      min-width:980px;
      border-collapse:separate;
      border-spacing:0;
      background:#fff;
      table-layout:fixed;
    }

    .yt-table thead th{
      position:sticky;
      top:0;
      z-index:5;
      background:#e2e8f0;
      border-bottom:1px solid #cbd5e1;
      border-right:1px solid #cbd5e1;
      padding:6px;
      font-size:9px;
      text-transform:uppercase;
      letter-spacing:.04em;
      font-weight:950;
      color:#334155;
      text-align:left;
      white-space:nowrap;
    }

    .yt-table tbody td{
      border-bottom:1px solid #e2e8f0;
      border-right:1px solid #eef2f7;
      padding:5px 6px;
      font-size:12px;
      color:#0f172a;
      vertical-align:middle;
      height:34px;
    }

    .yt-table tbody tr:hover{
      background:#f8fafc;
    }

    .yt-col-product{
      width:32%;
    }

    .yt-col-color{
      width:11%;
    }

    .yt-col-price{
      width:10%;
    }

    .yt-col-sec{
      width:15%;
    }

    .yt-col-primary{
      width:12%;
    }

    .yt-col-amount{
      width:14%;
    }

    .yt-product-mini{
      display:flex;
      align-items:center;
      gap:7px;
      min-width:0;
    }

    .yt-color-dot{
      width:9px;
      height:9px;
      min-width:9px;
      border-radius:999px;
      background:#64748b;
      box-shadow:0 0 0 3px #f1f5f9;
    }

    .yt-product-name{
      max-width:100%;
      font-size:12px;
      font-weight:950;
      color:#0f172a;
      line-height:1.15;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    }

    .yt-money{
      font-weight:950;
      color:#0f172a;
      font-variant-numeric:tabular-nums;
      white-space:nowrap;
    }

    .yt-muted{
      font-size:9px;
      color:#64748b;
      font-weight:800;
      margin-top:1px;
      line-height:1.1;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    }

    .yt-cell-input{
      width:98px;
      height:28px;
      border:1px solid #cbd5e1;
      border-radius:8px;
      padding:0 8px;
      outline:none;
      background:#fff;
      color:#0f172a;
      font-size:12px;
      font-weight:900;
      font-variant-numeric:tabular-nums;
    }

    .yt-cell-input:focus{
      border-color:#16a34a;
      box-shadow:0 0 0 2px rgba(22,163,74,.12);
    }

    .yt-total-cell{
      font-size:12px;
      font-weight:950;
      color:#047857;
      font-variant-numeric:tabular-nums;
      white-space:nowrap;
    }

    .yt-empty{
      padding:22px;
      text-align:center;
      color:#64748b;
      font-size:13px;
      font-weight:850;
    }

    .yt-cat-row td{
      background:#dbeafe!important;
      border-bottom:1px solid #bfdbfe!important;
      color:#1e3a8a!important;
      height:34px!important;
      padding:0!important;
    }

    .yt-sub-row td{
      background:#f8fafc!important;
      border-bottom:1px solid #e2e8f0!important;
      color:#334155!important;
      height:30px!important;
      padding:0!important;
    }

    .yt-group-btn{
      width:100%;
      height:100%;
      border:0;
      background:transparent;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:8px;
      padding:0 8px;
      cursor:pointer;
      text-align:left;
      color:inherit;
    }

    .yt-group-title{
      display:flex;
      align-items:center;
      gap:7px;
      min-width:0;
      font-size:12px;
      font-weight:950;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
    }

    .yt-group-title.sub{
      font-size:11px;
      color:#334155;
      padding-left:15px;
    }

    .yt-arrow{
      width:18px;
      height:18px;
      border-radius:6px;
      background:rgba(255,255,255,.65);
      display:inline-flex;
      align-items:center;
      justify-content:center;
      font-size:10px;
      font-weight:950;
      flex:0 0 auto;
    }

    .yt-group-summary{
      display:flex;
      align-items:center;
      gap:8px;
      font-size:10px;
      font-weight:950;
      color:inherit;
      white-space:nowrap;
      font-variant-numeric:tabular-nums;
    }

    .yt-month-head{
      padding:7px;
      background:#fff;
      border-bottom:1px solid #e2e8f0;
      display:grid;
      grid-template-columns:repeat(4,minmax(0,1fr));
      gap:7px;
      position:sticky;
      top:0;
      z-index:3;
    }

    .yt-summary-box{
      background:#f8fafc;
      border:1px solid #e2e8f0;
      border-radius:10px;
      padding:7px;
      min-height:48px;
    }

    .yt-summary-box label{
      display:block;
      font-size:9px;
      font-weight:950;
      color:#64748b;
      text-transform:uppercase;
      line-height:1;
      margin-bottom:4px;
    }

    .yt-summary-box b{
      display:block;
      font-size:14px;
      font-weight:950;
      color:#0f172a;
      line-height:1;
      font-variant-numeric:tabular-nums;
    }

    .yt-month-grid{
      padding:7px;
      display:grid;
      grid-template-columns:repeat(6,minmax(0,1fr));
      gap:6px;
    }

    .yt-month-card{
      border:1px solid #e2e8f0;
      background:linear-gradient(180deg,#fff,#f8fafc);
      border-radius:11px;
      padding:7px;
      min-height:82px;
      cursor:pointer;
      transition:.15s ease;
      display:flex;
      flex-direction:column;
      justify-content:space-between;
      gap:4px;
      text-align:left;
    }

    .yt-month-card:hover{
      transform:translateY(-1px);
      border-color:#93c5fd;
      box-shadow:0 8px 18px rgba(15,23,42,.07);
    }

    .yt-month-card.edited{
      border-color:#bbf7d0;
      background:linear-gradient(180deg,#ecfdf5,#fff);
    }

    .yt-month-card.completed{
      background:#e5e7eb;
      border-color:#cbd5e1;
      color:#64748b;
      cursor:not-allowed;
      opacity:.86;
      box-shadow:none;
      transform:none;
    }

    .yt-month-card.completed:hover{
      transform:none;
      border-color:#cbd5e1;
      box-shadow:none;
    }

    .yt-month-card.completed .yt-month-name,
    .yt-month-card.completed .yt-month-total{
      color:#64748b;
    }

    .yt-month-name{
      font-size:13px;
      font-weight:950;
      color:#0f172a;
      line-height:1;
    }

    .yt-month-total{
      font-size:14px;
      font-weight:950;
      color:#047857;
      font-variant-numeric:tabular-nums;
    }

    .yt-month-meta{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:5px;
      font-size:9px;
      font-weight:900;
      color:#64748b;
    }

    .yt-chip{
      display:inline-flex;
      align-items:center;
      height:18px;
      padding:0 6px;
      border-radius:999px;
      font-size:8px;
      font-weight:950;
      white-space:nowrap;
      background:#eff6ff;
      border:1px solid #bfdbfe;
      color:#1d4ed8;
    }

    .yt-chip.green{
      background:#ecfdf5;
      border-color:#bbf7d0;
      color:#047857;
    }

    .yt-chip.gray{
      background:#f1f5f9;
      border-color:#cbd5e1;
      color:#64748b;
    }

    .yt-modal-backdrop{
      position:fixed;
      inset:0;
      background:rgba(15,23,42,.48);
      display:flex;
      align-items:center;
      justify-content:center;
      padding:0;
      z-index:9999;
    }

    .yt-modal{
      width:100vw;
      height:100vh;
      max-height:100vh;
      overflow:hidden;
      background:#fff;
      border-radius:0;
      border:none;
      box-shadow:none;
      display:flex;
      flex-direction:column;
    }

    .yt-modal .yt-table{
      min-width:980px;
    }

    .yt-modal .yt-table thead th{
      top:0;
    }

    .yt-modal-head{
      padding:8px 10px;
      border-bottom:1px solid #e2e8f0;
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap:8px;
      flex:0 0 auto;
      background:#fff;
    }

    .yt-modal-title{
      margin:0;
      font-size:16px;
      font-weight:950;
      color:#0f172a;
    }

    .yt-modal-sub{
      margin:3px 0 0 0;
      font-size:11px;
      color:#64748b;
      font-weight:800;
    }

    .yt-close{
      height:30px;
      width:30px;
      border:none;
      border-radius:8px;
      background:#f1f5f9;
      color:#0f172a;
      font-size:18px;
      font-weight:950;
      cursor:pointer;
    }

    .yt-month-edit-filter{
      display:grid;
      grid-template-columns:150px 150px 130px minmax(180px,1fr) 150px;
      gap:5px;
      align-items:end;
      padding:5px 7px;
      background:#fff;
      border-bottom:1px solid #dbe3ef;
      flex:0 0 auto;
    }

    .yt-modal-body{
      flex:1 1 auto;
      min-height:0;
      overflow:auto;
      padding:0;
      background:#fff;
    }

    .yt-modal-foot{
      padding:8px 10px;
      border-top:1px solid #e2e8f0;
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap:8px;
      flex:0 0 auto;
      background:#f8fafc;
    }

    @media(max-width:1250px){
      .yt-month-grid{
        grid-template-columns:repeat(4,minmax(0,1fr));
      }

      .yt-filter{
        grid-template-columns:repeat(3,minmax(0,1fr));
      }

      .yt-month-edit-filter{
        grid-template-columns:repeat(3,minmax(0,1fr));
      }
    }

    @media(max-width:760px){
      .yt-page{
        overflow:auto;
        height:auto;
        min-height:100vh;
      }

      .yt-main{
        overflow:visible;
        display:block;
      }

      .yt-create-area,
      .yt-month-area{
        overflow:auto;
      }

      .yt-filter{
        grid-template-columns:1fr;
      }

      .yt-table{
        min-width:920px;
      }

      .yt-title span{
        display:none;
      }

      .yt-month-grid,
      .yt-month-head{
        grid-template-columns:1fr;
      }

      .yt-month-edit-filter{
        grid-template-columns:1fr;
      }

      .yt-modal-foot{
        align-items:flex-start;
        flex-direction:column;
      }
    }
  `;

  document.head.appendChild(style);
};

export default function YearlyTarget() {
  const [fy, setFy] = useState(getCurrentFY());
  const [productList, setProductList] = useState([]);
  const [savedTargets, setSavedTargets] = useState([]);

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMonth, setSavingMonth] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(FILTER_ALL);
  const [subCategoryFilter, setSubCategoryFilter] = useState(FILTER_ALL);
  const [colorFilter, setColorFilter] = useState(FILTER_ALL);

  const [secondaryQtyMap, setSecondaryQtyMap] = useState({});
  const [editingTargetId, setEditingTargetId] = useState("");

  const [expandedCats, setExpandedCats] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});

  const [monthEditor, setMonthEditor] = useState(null);
  const [monthQtyMap, setMonthQtyMap] = useState({});
  const [monthSearch, setMonthSearch] = useState("");
  const [monthCategoryFilter, setMonthCategoryFilter] = useState(FILTER_ALL);
  const [monthSubCategoryFilter, setMonthSubCategoryFilter] =
    useState(FILTER_ALL);
  const [monthColorFilter, setMonthColorFilter] = useState(FILTER_ALL);
  const [monthExpandedCats, setMonthExpandedCats] = useState({});
  const [monthExpandedSubs, setMonthExpandedSubs] = useState({});

  const currentUser = getUser();
  const database = currentUser.database || currentUser.db || "";
  const loading = loadingProducts || loadingTargets;

  useEffect(() => {
    injectOnce();
  }, []);

  useEffect(() => {
    if (!database) return;

    setExpandedCats(safeReadLS(expandKey(database, fy, "year-cats"), {}));
    setExpandedSubs(safeReadLS(expandKey(database, fy, "year-subs"), {}));
    setMonthExpandedCats(safeReadLS(expandKey(database, fy, "month-cats"), {}));
    setMonthExpandedSubs(safeReadLS(expandKey(database, fy, "month-subs"), {}));
  }, [database, fy]);

  useEffect(() => {
    loadProducts();
    loadSavedTargets();
  }, []);

  useEffect(() => {
    if (!database) return;
    safeWriteLS(expandKey(database, fy, "year-cats"), expandedCats);
  }, [database, fy, expandedCats]);

  useEffect(() => {
    if (!database) return;
    safeWriteLS(expandKey(database, fy, "year-subs"), expandedSubs);
  }, [database, fy, expandedSubs]);

  useEffect(() => {
    if (!database) return;
    safeWriteLS(expandKey(database, fy, "month-cats"), monthExpandedCats);
  }, [database, fy, monthExpandedCats]);

  useEffect(() => {
    if (!database) return;
    safeWriteLS(expandKey(database, fy, "month-subs"), monthExpandedSubs);
  }, [database, fy, monthExpandedSubs]);

  const loadProducts = async () => {
    const db = getUser().database || getUser().db || "";

    if (!db) {
      setProductList([]);
      return;
    }

    const cached = safeReadLS(productsCacheKey(db), null);
    if (cached?.items?.length) setProductList(cached.items);

    try {
      setLoadingProducts(true);

      const res = await axios.get(`${PurchaseProductList_Product}${db}`);
      const payload = res?.data ?? res;

      const list = arr(
        payload.Product || payload.product || payload.data,
      ).filter((p) => !norm(p.status) || norm(p.status) === "active");

      setProductList(list);

      safeWriteLS(productsCacheKey(db), {
        updatedAt: new Date().toISOString(),
        items: list,
      });
    } catch (err) {
      console.log("[YearlyTarget] loadProducts error:", err);

      if (!cached?.items?.length) setProductList([]);

      Swal.fire({
        icon: "error",
        title: "Product Load Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to load product list.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadSavedTargets = async () => {
    const db = getUser().database || getUser().db || "";

    if (!db) {
      setSavedTargets([]);
      return;
    }

    const cached = safeReadLS(targetsCacheKey(db), null);
    if (cached?.items?.length) setSavedTargets(cached.items);

    try {
      setLoadingTargets(true);

      const res = await axios.get(`${API.list}/${db}`);
      const payload = res?.data ?? res;
      const list = arr(payload.data || payload.YearlyTarget || []);

      setSavedTargets(list);

      safeWriteLS(targetsCacheKey(db), {
        updatedAt: new Date().toISOString(),
        items: list,
      });
    } catch (err) {
      console.log("[YearlyTarget] loadSavedTargets error:", err);

      if (!cached?.items?.length) setSavedTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const currentFYTarget = useMemo(() => {
    return (
      savedTargets.find(
        (t) =>
          String(t.fyear) === String(fy) &&
          norm(t.status || "Active") !== "deleted",
      ) || null
    );
  }, [savedTargets, fy]);

  const pageMode = currentFYTarget && !editingTargetId ? "months" : "create";

  useEffect(() => {
    if (!database || pageMode !== "create") return;

    const draft = safeReadLS(
      formDraftKey(database, fy, editingTargetId || "new"),
      null,
    );

    if (!draft) return;

    setSecondaryQtyMap(draft.secondaryQtyMap || {});
    setSearch(draft.search || "");
    setCategoryFilter(draft.categoryFilter || FILTER_ALL);
    setSubCategoryFilter(draft.subCategoryFilter || FILTER_ALL);
    setColorFilter(draft.colorFilter || FILTER_ALL);
  }, [database, fy, editingTargetId, pageMode]);

  useEffect(() => {
    if (!database || pageMode !== "create") return;

    const t = setTimeout(() => {
      safeWriteLS(formDraftKey(database, fy, editingTargetId || "new"), {
        fy,
        editingTargetId: editingTargetId || "",
        secondaryQtyMap,
        search,
        categoryFilter,
        subCategoryFilter,
        colorFilter,
        updatedAt: new Date().toISOString(),
      });
    }, 120);

    return () => clearTimeout(t);
  }, [
    database,
    fy,
    editingTargetId,
    secondaryQtyMap,
    search,
    categoryFilter,
    subCategoryFilter,
    colorFilter,
    pageMode,
  ]);

  const categoryOptions = useMemo(
    () => makeOptionList(productList, getProductCategory),
    [productList],
  );

  const subCategoryOptions = useMemo(() => {
    const rows =
      categoryFilter === FILTER_ALL
        ? productList
        : productList.filter(
            (p) => norm(getProductCategory(p)) === norm(categoryFilter),
          );

    return makeOptionList(rows, getProductSubCategory);
  }, [productList, categoryFilter]);

  const colorOptions = useMemo(() => {
    let rows = productList;

    if (categoryFilter !== FILTER_ALL) {
      rows = rows.filter(
        (p) => norm(getProductCategory(p)) === norm(categoryFilter),
      );
    }

    if (subCategoryFilter !== FILTER_ALL) {
      rows = rows.filter(
        (p) => norm(getProductSubCategory(p)) === norm(subCategoryFilter),
      );
    }

    return makeOptionList(rows, getProductColor);
  }, [productList, categoryFilter, subCategoryFilter]);

  const allRows = useMemo(
    () => productList.map((p) => productToRow(p, secondaryQtyMap)),
    [productList, secondaryQtyMap],
  );

  const rows = useMemo(() => {
    const q = norm(search);

    return allRows.filter((row) => {
      const categoryOk =
        categoryFilter === FILTER_ALL ||
        norm(row.category) === norm(categoryFilter);

      const subOk =
        subCategoryFilter === FILTER_ALL ||
        norm(row.subCategory) === norm(subCategoryFilter);

      const colorOk =
        colorFilter === FILTER_ALL || norm(row.color) === norm(colorFilter);

      const searchText = [
        row.productName,
        row.category,
        row.subCategory,
        row.color,
        row.product?.sId,
        row.product?.barcode,
        row.product?.Barcode,
        row.product?.HSN_Code,
        row.product?.hsnCode,
      ]
        .map(str)
        .join(" ")
        .toLowerCase();

      return categoryOk && subOk && colorOk && (!q || searchText.includes(q));
    });
  }, [allRows, search, categoryFilter, subCategoryFilter, colorFilter]);

  const groupedRows = useMemo(() => makeGroups(rows), [rows]);

  const filledRows = useMemo(
    () => allRows.filter((row) => num(row.secondaryQty) > 0),
    [allRows],
  );

  const grandTotal = useMemo(
    () => filledRows.reduce((sum, row) => sum + num(row.total), 0),
    [filledRows],
  );

  const totalSecondaryQty = useMemo(
    () => filledRows.reduce((sum, row) => sum + num(row.secondaryQty), 0),
    [filledRows],
  );

  const totalPrimaryQty = useMemo(
    () => filledRows.reduce((sum, row) => sum + num(row.primaryQty), 0),
    [filledRows],
  );

  const currentMonthlyTargets = useMemo(
    () => (currentFYTarget ? getMonthlyTargets(currentFYTarget) : []),
    [currentFYTarget],
  );

  const currentFYGrandTotal = num(currentFYTarget?.grandTotal);
  const currentFYMonthlyAverage = r2(currentFYGrandTotal / 12);

  const monthProductSource = useMemo(() => {
    if (!monthEditor) return [];

    const map = new Map();

    productList.forEach((product) => {
      const id = getProductId(product);
      if (id) map.set(id, product);
    });

    getMonthProducts(monthEditor?.monthRow).forEach((product) => {
      const id = str(product?.productId || product?._id || product?.id);
      if (!id) return;

      if (!map.has(id)) {
        map.set(id, savedProductToProductMasterShape(product));
      }
    });

    return Array.from(map.values());
  }, [monthEditor, productList]);

  const allMonthRows = useMemo(() => {
    return monthProductSource.map((product) => {
      const id =
        getProductId(product) || str(product?.productId || product?._id);

      return productToRow(
        {
          ...product,
          _id: id,
          id,
          productId: id,
        },
        monthQtyMap,
      );
    });
  }, [monthProductSource, monthQtyMap]);

  const monthCategoryOptions = useMemo(
    () => makeOptionList(monthProductSource, getProductCategory),
    [monthProductSource],
  );

  const monthSubCategoryOptions = useMemo(() => {
    const sourceRows =
      monthCategoryFilter === FILTER_ALL
        ? monthProductSource
        : monthProductSource.filter(
            (p) => norm(getProductCategory(p)) === norm(monthCategoryFilter),
          );

    return makeOptionList(sourceRows, getProductSubCategory);
  }, [monthProductSource, monthCategoryFilter]);

  const monthColorOptions = useMemo(() => {
    let sourceRows = monthProductSource;

    if (monthCategoryFilter !== FILTER_ALL) {
      sourceRows = sourceRows.filter(
        (p) => norm(getProductCategory(p)) === norm(monthCategoryFilter),
      );
    }

    if (monthSubCategoryFilter !== FILTER_ALL) {
      sourceRows = sourceRows.filter(
        (p) => norm(getProductSubCategory(p)) === norm(monthSubCategoryFilter),
      );
    }

    return makeOptionList(sourceRows, getProductColor);
  }, [monthProductSource, monthCategoryFilter, monthSubCategoryFilter]);

  const monthRows = useMemo(() => {
    const q = norm(monthSearch);

    return allMonthRows.filter((row) => {
      const categoryOk =
        monthCategoryFilter === FILTER_ALL ||
        norm(row.category) === norm(monthCategoryFilter);

      const subOk =
        monthSubCategoryFilter === FILTER_ALL ||
        norm(row.subCategory) === norm(monthSubCategoryFilter);

      const colorOk =
        monthColorFilter === FILTER_ALL ||
        norm(row.color) === norm(monthColorFilter);

      const searchText = [
        row.productName,
        row.category,
        row.subCategory,
        row.color,
        row.product?.sId,
        row.product?.barcode,
        row.product?.Barcode,
        row.product?.HSN_Code,
        row.product?.hsnCode,
      ]
        .map(str)
        .join(" ")
        .toLowerCase();

      return categoryOk && subOk && colorOk && (!q || searchText.includes(q));
    });
  }, [
    allMonthRows,
    monthSearch,
    monthCategoryFilter,
    monthSubCategoryFilter,
    monthColorFilter,
  ]);

  const monthGroupedRows = useMemo(() => makeGroups(monthRows), [monthRows]);

  const monthTotal = useMemo(
    () => allMonthRows.reduce((sum, row) => sum + num(row.total), 0),
    [allMonthRows],
  );

  const monthVisibleTotal = useMemo(
    () => monthRows.reduce((sum, row) => sum + num(row.total), 0),
    [monthRows],
  );

  useEffect(() => {
    if (!monthEditor?.monthRow?.month || !database) return;

    const t = setTimeout(() => {
      safeWriteLS(monthDraftKey(database, fy, monthEditor.monthRow.month), {
        qtyMap: monthQtyMap,
        search: monthSearch,
        categoryFilter: monthCategoryFilter,
        subCategoryFilter: monthSubCategoryFilter,
        colorFilter: monthColorFilter,
        updatedAt: new Date().toISOString(),
      });
    }, 120);

    return () => clearTimeout(t);
  }, [
    database,
    fy,
    monthEditor,
    monthQtyMap,
    monthSearch,
    monthCategoryFilter,
    monthSubCategoryFilter,
    monthColorFilter,
  ]);

  const clearFilters = () => {
    setSearch("");
    setCategoryFilter(FILTER_ALL);
    setSubCategoryFilter(FILTER_ALL);
    setColorFilter(FILTER_ALL);
  };

  const resetMonthFilters = () => {
    setMonthSearch("");
    setMonthCategoryFilter(FILTER_ALL);
    setMonthSubCategoryFilter(FILTER_ALL);
    setMonthColorFilter(FILTER_ALL);
  };

  const resetForm = () => {
    setSecondaryQtyMap({});
    setEditingTargetId("");
  };

  const setSecondaryQty = (productId, value) => {
    setSecondaryQtyMap((prev) => ({
      ...prev,
      [productId]: Math.max(num(value), 0),
    }));
  };

  const setMonthSecondaryQty = (productId, value) => {
    setMonthQtyMap((prev) => ({
      ...prev,
      [productId]: Math.max(num(value), 0),
    }));
  };

  const makeYearPayload = () => {
    const productTargets = makeProductPayload(filledRows);

    return {
      database,
      fyear: fy,
      targetName: `Yearly Target ${fy}`,

      created_by: currentUser._id || currentUser.id || "",
      updated_by: currentUser._id || currentUser.id || "",

      filters: {
        category: categoryFilter === FILTER_ALL ? "" : categoryFilter,
        subCategory: subCategoryFilter === FILTER_ALL ? "" : subCategoryFilter,
        color: colorFilter === FILTER_ALL ? "" : colorFilter,
        search,
      },

      grandTotal: Number(grandTotal || 0),
      totalSecondaryQty: Number(totalSecondaryQty || 0),
      totalPrimaryQty: Number(totalPrimaryQty || 0),

      productTargets,
      products: productTargets,
    };
  };

  const saveYearlyTarget = async () => {
    try {
      if (!database) {
        Swal.fire({
          icon: "warning",
          title: "Database Missing",
          text: "Database not found in logged-in user data.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      if (!filledRows.length) {
        Swal.fire({
          icon: "warning",
          title: "No Target Entered",
          text: "Please enter secondary quantity for at least one product.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      if (currentFYTarget && !editingTargetId) {
        Swal.fire({
          icon: "warning",
          title: "Target Already Created",
          text: "Yearly target is already created for this financial year. Please edit/update the existing target.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      setSaving(true);

      const payload = makeYearPayload();

      const optimisticTarget = {
        ...(editingTargetId ? currentFYTarget || {} : {}),
        ...payload,
        _id: editingTargetId || currentFYTarget?._id || `LOCAL-${Date.now()}`,
        monthlyTargets: getMonthlyTargets(payload),
        localPendingSync: true,
      };

      const optimisticList = upsertTarget(savedTargets, optimisticTarget);

      setSavedTargets(optimisticList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: optimisticList,
      });

      const res = editingTargetId
        ? await axios.put(`${API.update}/${editingTargetId}`, payload)
        : await axios.post(API.create, payload);

      const apiPayload = res?.data ?? res;

      const saved =
        apiPayload?.data ||
        apiPayload?.YearlyTarget ||
        apiPayload?.target ||
        apiPayload?.result ||
        null;

      if (saved) {
        const finalList = upsertTarget(optimisticList, saved);

        setSavedTargets(finalList);

        safeWriteLS(targetsCacheKey(database), {
          updatedAt: new Date().toISOString(),
          items: finalList,
        });
      }

      safeRemoveLS(formDraftKey(database, fy, editingTargetId || "new"));

      Swal.fire({
        icon: "success",
        title: editingTargetId
          ? "Yearly Target Updated"
          : "Yearly Target Created",
        text: editingTargetId
          ? "Yearly target was updated and month split was regenerated."
          : "Yearly target was created and split month-wise from April to March.",
        confirmButtonColor: "#16a34a",
      });

      resetForm();
      loadSavedTargets();
    } catch (err) {
      console.log("[YearlyTarget] save error:", err);

      const exists =
        err?.response?.status === 409 ||
        err?.response?.data?.code === "TARGET_ALREADY_CREATED";

      Swal.fire({
        icon: exists ? "warning" : "error",
        title: exists ? "Target Already Created" : "Save Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to save yearly target. Your local draft is still saved.",
        confirmButtonColor: exists ? "#f59e0b" : "#dc2626",
      });

      loadSavedTargets();
    } finally {
      setSaving(false);
    }
  };

  const loadTargetIntoForm = (target) => {
    const map = {};

    getSavedProducts(target).forEach((p) => {
      const id = str(p.productId);
      if (id) map[id] = num(p.secondaryQty || p.sQty);
    });

    setFy(target.fyear || getCurrentFY());
    setSecondaryQtyMap(map);
    setSearch(target?.filters?.search || "");
    setCategoryFilter(target?.filters?.category || FILTER_ALL);
    setSubCategoryFilter(target?.filters?.subCategory || FILTER_ALL);
    setColorFilter(target?.filters?.color || FILTER_ALL);
    setEditingTargetId(target._id || "");
  };

  const deleteTarget = async (target) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Target?",
      text: "This yearly target and its monthly split will be removed.",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    const previousList = savedTargets;
    const nextList = savedTargets.filter((item) => item?._id !== target?._id);

    setSavedTargets(nextList);

    safeWriteLS(targetsCacheKey(database), {
      updatedAt: new Date().toISOString(),
      items: nextList,
    });

    try {
      await axios.delete(`${API.delete}/${target?._id}/${database}`);

      if (editingTargetId === target?._id) resetForm();

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Yearly target deleted successfully.",
        confirmButtonColor: "#16a34a",
      });

      loadSavedTargets();
    } catch (err) {
      console.log("[YearlyTarget] delete error:", err);

      setSavedTargets(previousList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: previousList,
      });

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to delete yearly target.",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  const openMonthEditor = (monthRow) => {
    if (isMonthCompleted(fy, monthRow.month)) {
      Swal.fire({
        icon: "info",
        title: "Month Locked",
        text: `${monthRow.month} is completed, so this month target cannot be edited.`,
        confirmButtonColor: "#64748b",
      });
      return;
    }

    const draft = safeReadLS(monthDraftKey(database, fy, monthRow.month), null);
    const map = {};

    getMonthProducts(monthRow).forEach((p) => {
      const id = str(p.productId || p._id || p.id);
      if (id) map[id] = num(p.secondaryQty || p.sQty);
    });

    setMonthQtyMap(draft?.qtyMap || map);
    setMonthSearch(draft?.search || "");
    setMonthCategoryFilter(draft?.categoryFilter || FILTER_ALL);
    setMonthSubCategoryFilter(draft?.subCategoryFilter || FILTER_ALL);
    setMonthColorFilter(draft?.colorFilter || FILTER_ALL);
    setMonthEditor({ target: currentFYTarget, monthRow });

    if (currentFYTarget?._id && database) {
      axios
        .get(`${API.view}/${currentFYTarget._id}/${database}`)
        .then((res) => {
          const latestTarget = (res?.data ?? res)?.data || currentFYTarget;

          const latestMonth =
            getMonthlyTargets(latestTarget).find(
              (m) => norm(m.month) === norm(monthRow.month),
            ) || monthRow;

          if (!draft?.qtyMap) {
            const latestMap = {};

            getMonthProducts(latestMonth).forEach((p) => {
              const id = str(p.productId || p._id || p.id);
              if (id) latestMap[id] = num(p.secondaryQty || p.sQty);
            });

            setMonthQtyMap(latestMap);
          }

          setMonthEditor({ target: latestTarget, monthRow: latestMonth });
        })
        .catch((err) => {
          console.log("[YearlyTarget] latest month fetch skipped:", err);
        });
    }
  };

  const updateLocalMonthTarget = (target, monthName, productTargets) => {
    const startIndex = FY_MONTHS.indexOf(monthName);

    const editedTotal = r2(
      productTargets.reduce((sum, p) => sum + num(p.total), 0),
    );

    const monthlyTargets = getMonthlyTargets(target).map((monthRow) => {
      const idx = FY_MONTHS.indexOf(monthRow.month);

      const shouldUpdate =
        idx >= startIndex && !isMonthCompleted(fy, monthRow.month);

      if (!shouldUpdate) return monthRow;

      return {
        ...monthRow,
        targetTotal: editedTotal,
        productTargets,
        products: productTargets,
        isEdited: true,
        editedAt: new Date().toISOString(),
        edited_by: currentUser._id || currentUser.id || "",
      };
    });

    const yearProducts = aggregateProductsFromMonths(monthlyTargets);

    const yearTotal = r2(
      monthlyTargets.reduce(
        (sum, monthRow) => sum + num(monthRow.targetTotal),
        0,
      ),
    );

    return {
      ...target,
      monthlyTargets,
      productTargets: yearProducts,
      products: yearProducts,
      grandTotal: yearTotal,
      targetTotal: yearTotal,
      updatedAt: new Date().toISOString(),
    };
  };

  const saveMonthTarget = async () => {
    try {
      if (!monthEditor?.target?._id || !monthEditor?.monthRow?.month) return;

      if (isMonthCompleted(fy, monthEditor.monthRow.month)) {
        Swal.fire({
          icon: "info",
          title: "Month Locked",
          text: `${monthEditor.monthRow.month} is completed, so this month target cannot be edited.`,
          confirmButtonColor: "#64748b",
        });
        return;
      }

      const productTargets = makeProductPayload(
        allMonthRows.filter((row) => num(row.secondaryQty) > 0),
      );

      if (!productTargets.length) {
        Swal.fire({
          icon: "warning",
          title: "No Month Target",
          text: "Please enter target quantity for at least one product.",
          confirmButtonColor: "#f59e0b",
        });
        return;
      }

      setSavingMonth(true);

      const optimisticTarget = updateLocalMonthTarget(
        monthEditor.target,
        monthEditor.monthRow.month,
        productTargets,
      );

      const optimisticList = upsertTarget(savedTargets, optimisticTarget);

      setSavedTargets(optimisticList);

      safeWriteLS(targetsCacheKey(database), {
        updatedAt: new Date().toISOString(),
        items: optimisticList,
      });

      await axios.put(`${API.updateMonth}/${monthEditor.target._id}`, {
        database,
        fyear: fy,
        month: monthEditor.monthRow.month,
        productTargets,
        products: productTargets,
        updated_by: currentUser._id || currentUser.id || "",
        edited_by: currentUser._id || currentUser.id || "",
      });

      safeRemoveLS(monthDraftKey(database, fy, monthEditor.monthRow.month));

      Swal.fire({
        icon: "success",
        title: "Month Updated",
        text: `${monthEditor.monthRow.month} target updated successfully. Editable upcoming months also updated.`,
        confirmButtonColor: "#16a34a",
      });

      setMonthEditor(null);
      setMonthQtyMap({});
      resetMonthFilters();
      loadSavedTargets();
    } catch (err) {
      console.log("[YearlyTarget] saveMonthTarget error:", err);

      Swal.fire({
        icon: "error",
        title: "Month Update Failed",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to update month target. Your local month draft is still saved.",
        confirmButtonColor: "#dc2626",
      });

      loadSavedTargets();
    } finally {
      setSavingMonth(false);
    }
  };

  const toggleCat = (catKey, mode = "year") => {
    if (mode === "month") {
      setMonthExpandedCats((prev) => ({
        ...prev,
        [catKey]: !(prev[catKey] !== false),
      }));
      return;
    }

    setExpandedCats((prev) => ({
      ...prev,
      [catKey]: !(prev[catKey] !== false),
    }));
  };

  const toggleSub = (catKey, subKey, mode = "year") => {
    const key = `${catKey}||${subKey}`;

    if (mode === "month") {
      setMonthExpandedSubs((prev) => ({
        ...prev,
        [key]: !(prev[key] !== false),
      }));
      return;
    }

    setExpandedSubs((prev) => ({
      ...prev,
      [key]: !(prev[key] !== false),
    }));
  };

  const expandAllGroups = (groups, mode = "year") => {
    const cats = {};
    const subs = {};

    groups.forEach((cat) => {
      cats[cat.key] = true;

      cat.subGroups.forEach((sub) => {
        subs[`${cat.key}||${sub.key}`] = true;
      });
    });

    if (mode === "month") {
      setMonthExpandedCats(cats);
      setMonthExpandedSubs(subs);
      return;
    }

    setExpandedCats(cats);
    setExpandedSubs(subs);
  };

  const collapseAllGroups = (groups, mode = "year") => {
    const cats = {};
    const subs = {};

    groups.forEach((cat) => {
      cats[cat.key] = false;

      cat.subGroups.forEach((sub) => {
        subs[`${cat.key}||${sub.key}`] = false;
      });
    });

    if (mode === "month") {
      setMonthExpandedCats(cats);
      setMonthExpandedSubs(subs);
      return;
    }

    setExpandedCats(cats);
    setExpandedSubs(subs);
  };

  const renderProductTable = ({
    groups,
    qtyMap,
    onQtyChange,
    expandedCategoryMap,
    expandedSubMap,
    mode = "year",
    emptyText = "No products found.",
  }) => (
    <table className="yt-table">
      <thead>
        <tr>
          <th className="yt-col-product">Product</th>
          <th className="yt-col-color">Color</th>
          <th className="yt-col-price">Price</th>
          <th className="yt-col-sec">Secondary Qty</th>
          <th className="yt-col-primary">Primary Qty</th>
          <th className="yt-col-amount">Amount</th>
        </tr>
      </thead>

      <tbody>
        {loadingProducts && mode === "year" ? (
          <tr>
            <td colSpan={6} className="yt-empty">
              Loading products...
            </td>
          </tr>
        ) : groups.length === 0 ? (
          <tr>
            <td colSpan={6} className="yt-empty">
              {emptyText}
            </td>
          </tr>
        ) : (
          groups.map((cat) => {
            const catOpen = expandedCategoryMap[cat.key] !== false;

            return (
              <React.Fragment key={cat.key}>
                <tr className="yt-cat-row">
                  <td colSpan={6}>
                    <button
                      type="button"
                      className="yt-group-btn"
                      onClick={() => toggleCat(cat.key, mode)}
                    >
                      <span className="yt-group-title">
                        <span className="yt-arrow">{catOpen ? "−" : "+"}</span>
                        {cat.label}
                      </span>

                      <span className="yt-group-summary">
                        <span>{cat.count} Products</span>
                        <span>{Number(cat.qty).toFixed(2)} Qty</span>
                        <span>{money(cat.total)}</span>
                      </span>
                    </button>
                  </td>
                </tr>

                {catOpen
                  ? cat.subGroups.map((sub) => {
                      const subStateKey = `${cat.key}||${sub.key}`;
                      const subOpen = expandedSubMap[subStateKey] !== false;

                      return (
                        <React.Fragment key={subStateKey}>
                          <tr className="yt-sub-row">
                            <td colSpan={6}>
                              <button
                                type="button"
                                className="yt-group-btn"
                                onClick={() =>
                                  toggleSub(cat.key, sub.key, mode)
                                }
                              >
                                <span className="yt-group-title sub">
                                  <span className="yt-arrow">
                                    {subOpen ? "−" : "+"}
                                  </span>
                                  {sub.label}
                                </span>

                                <span className="yt-group-summary">
                                  <span>{sub.count} Products</span>
                                  <span>{Number(sub.qty).toFixed(2)} Qty</span>
                                  <span>{money(sub.total)}</span>
                                </span>
                              </button>
                            </td>
                          </tr>

                          {subOpen
                            ? sub.rows.map((row) => (
                                <tr
                                  key={row.id}
                                  style={{
                                    backgroundColor:
                                      row.profitColorCode || undefined,
                                  }}
                                >
                                  <td>
                                    <div className="yt-product-mini">
                                      <span className="yt-color-dot" />
                                      <div className="yt-product-name">
                                        {row.productName}
                                      </div>
                                    </div>
                                  </td>

                                  <td>
                                    <div className="yt-money">{row.color}</div>
                                  </td>

                                  <td>
                                    <div className="yt-money">
                                      {money(row.saleRate)}
                                    </div>
                                  </td>

                                  <td>
                                    <input
                                      type="number"
                                      min="0"
                                      className="yt-cell-input"
                                      value={qtyMap[row.id] ?? ""}
                                      onChange={(e) =>
                                        onQtyChange(row.id, e.target.value)
                                      }
                                      placeholder="0"
                                    />

                                    <div className="yt-muted">
                                      {row.secondaryUnitName} ×{" "}
                                      {row.secondarySize}
                                    </div>
                                  </td>

                                  <td>
                                    <div className="yt-money">
                                      {Number(row.primaryQty || 0).toFixed(2)}
                                    </div>

                                    <div className="yt-muted">
                                      {row.primaryUnitName}
                                    </div>
                                  </td>

                                  <td>
                                    <div className="yt-total-cell">
                                      {money(row.total)}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            : null}
                        </React.Fragment>
                      );
                    })
                  : null}
              </React.Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );

  return (
    <div className="yt-page">
      <div className="yt-top">
        <div className="yt-title">
          <h2>Yearly Target</h2>
          <span>
            Local draft/cache is saved instantly. Database save remains active
            in the background action.
          </span>
        </div>

        <div className="yt-actions">
          {currentFYTarget && !editingTargetId ? (
            <button
              type="button"
              className="yt-btn blue"
              onClick={() => loadTargetIntoForm(currentFYTarget)}
            >
              Edit Yearly Target
            </button>
          ) : null}

          {editingTargetId ? (
            <button type="button" className="yt-btn warn" onClick={resetForm}>
              Cancel Yearly Edit
            </button>
          ) : null}

          {currentFYTarget ? (
            <button
              type="button"
              className="yt-btn danger"
              onClick={() => deleteTarget(currentFYTarget)}
            >
              Delete FY Target
            </button>
          ) : null}

          <button
            type="button"
            className="yt-btn light"
            onClick={loadProducts}
            disabled={loadingProducts}
          >
            Refresh Products
          </button>

          <button
            type="button"
            className="yt-btn light"
            onClick={loadSavedTargets}
            disabled={loadingTargets}
          >
            Refresh Target
          </button>

          <button type="button" className="yt-btn dark" onClick={clearFilters}>
            Clear Filters
          </button>

          {pageMode === "create" ? (
            <button
              type="button"
              className="yt-btn primary"
              onClick={saveYearlyTarget}
              disabled={saving || loading}
            >
              {saving
                ? "Saving..."
                : editingTargetId
                  ? "Update Yearly Target"
                  : "Create Yearly Target"}
            </button>
          ) : null}
        </div>
      </div>

      <div className="yt-filter">
        <div className="yt-field">
          <label className="yt-label">FY</label>

          <select
            className="yt-select"
            value={fy}
            onChange={(e) => {
              setFy(e.target.value);
              setEditingTargetId("");
              setSecondaryQtyMap({});
            }}
          >
            {getFYOptions().map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="yt-field">
          <label className="yt-label">Category</label>

          <select
            className="yt-select"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setSubCategoryFilter(FILTER_ALL);
              setColorFilter(FILTER_ALL);
            }}
            disabled={pageMode === "months"}
          >
            <option value={FILTER_ALL}>All Categories</option>

            {categoryOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="yt-field">
          <label className="yt-label">Sub Category</label>

          <select
            className="yt-select"
            value={subCategoryFilter}
            onChange={(e) => {
              setSubCategoryFilter(e.target.value);
              setColorFilter(FILTER_ALL);
            }}
            disabled={pageMode === "months"}
          >
            <option value={FILTER_ALL}>All Sub Categories</option>

            {subCategoryOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="yt-field">
          <label className="yt-label">Color</label>

          <select
            className="yt-select"
            value={colorFilter}
            onChange={(e) => setColorFilter(e.target.value)}
            disabled={pageMode === "months"}
          >
            <option value={FILTER_ALL}>All Colors</option>

            {colorOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="yt-field">
          <label className="yt-label">Search</label>

          <input
            className="yt-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Product / color / HSN"
            disabled={pageMode === "months"}
          />
        </div>

        <div className="yt-field">
          <label className="yt-label">
            {pageMode === "months" ? "FY Grand Total" : "Form Grand Total"}
          </label>

          <div className="yt-stat">
            <span>Total</span>
            <b>
              {money(pageMode === "months" ? currentFYGrandTotal : grandTotal)}
            </b>
          </div>
        </div>

        <div className="yt-field">
          <label className="yt-label">
            {pageMode === "months" ? "Avg Monthly" : "Form Monthly"}
          </label>

          <div className="yt-stat">
            <span>/ Month</span>

            <b>
              {money(
                pageMode === "months"
                  ? currentFYMonthlyAverage
                  : grandTotal / 12,
              )}
            </b>
          </div>
        </div>
      </div>

      <div className="yt-flowbar">
        <div className="yt-step-wrap">
          <span
            className={`yt-step ${
              pageMode === "create" ? "active" : currentFYTarget ? "done" : ""
            }`}
          >
            1. Create Target
          </span>

          <span className={`yt-step ${pageMode === "months" ? "active" : ""}`}>
            2. Month-wise Display
          </span>

          <span className={`yt-step ${monthEditor ? "active" : ""}`}>
            3. Month Edit / Update
          </span>
        </div>

        <div className="yt-actions">
          {pageMode === "create" ? (
            <>
              <button
                type="button"
                className="yt-btn soft"
                onClick={() => expandAllGroups(groupedRows, "year")}
              >
                Expand All
              </button>

              <button
                type="button"
                className="yt-btn soft"
                onClick={() => collapseAllGroups(groupedRows, "year")}
              >
                Collapse All
              </button>
            </>
          ) : null}

          <div className="yt-status-note">
            {currentFYTarget && !editingTargetId
              ? "Completed months are gray and locked. Editable month update applies to upcoming editable months."
              : editingTargetId
                ? `Editing yearly target for ${fy}. Saving will regenerate Apr-Mar split.`
                : `No target created for ${fy}. Fill category-wise product target and create once.`}
          </div>
        </div>
      </div>

      <div className="yt-main">
        {pageMode === "months" ? (
          <div className="yt-month-area">
            <div className="yt-month-head">
              <div className="yt-summary-box">
                <label>Financial Year</label>
                <b>{fy}</b>
              </div>

              <div className="yt-summary-box">
                <label>Yearly Target</label>
                <b>{money(currentFYGrandTotal)}</b>
              </div>

              <div className="yt-summary-box">
                <label>Average Monthly</label>
                <b>{money(currentFYMonthlyAverage)}</b>
              </div>

              <div className="yt-summary-box">
                <label>Products</label>
                <b>{getSavedProducts(currentFYTarget).length}</b>
              </div>
            </div>

            <div className="yt-month-grid">
              {currentMonthlyTargets.map((monthRow) => {
                const completed = isMonthCompleted(fy, monthRow.month);

                return (
                  <button
                    type="button"
                    key={monthRow.month}
                    disabled={completed}
                    className={`yt-month-card ${
                      monthRow.isEdited ? "edited" : ""
                    } ${completed ? "completed" : ""}`}
                    onClick={() => openMonthEditor(monthRow)}
                  >
                    <div>
                      <div className="yt-month-name">{monthRow.month}</div>

                      <div className="yt-muted">
                        {completed ? "Locked" : "Click to edit"}
                      </div>
                    </div>

                    <div className="yt-month-total">
                      {money(monthRow.targetTotal)}
                    </div>

                    <div className="yt-month-meta">
                      <span>{getMonthProducts(monthRow).length} Products</span>

                      <span
                        className={`yt-chip ${
                          completed ? "gray" : monthRow.isEdited ? "green" : ""
                        }`}
                      >
                        {completed
                          ? "Locked"
                          : monthRow.isEdited
                            ? "Updated"
                            : "Editable"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="yt-create-area">
            {renderProductTable({
              groups: groupedRows,
              qtyMap: secondaryQtyMap,
              onQtyChange: setSecondaryQty,
              expandedCategoryMap: expandedCats,
              expandedSubMap: expandedSubs,
              mode: "year",
              emptyText: "No products found.",
            })}
          </div>
        )}
      </div>

      {monthEditor ? (
        <div className="yt-modal-backdrop">
          <div className="yt-modal">
            <div className="yt-modal-head">
              <div>
                <h3 className="yt-modal-title">
                  Edit {monthEditor.monthRow.month} Target
                </h3>

                <p className="yt-modal-sub">
                  FY: {fy} • Month Total: {money(monthTotal)} • Visible Total:{" "}
                  {money(monthVisibleTotal)} • Local draft is saved while typing
                </p>
              </div>

              <button
                type="button"
                className="yt-close"
                onClick={() => {
                  setMonthEditor(null);
                  setMonthQtyMap({});
                  resetMonthFilters();
                }}
              >
                ×
              </button>
            </div>

            <div className="yt-month-edit-filter">
              <div className="yt-field">
                <label className="yt-label">Category</label>

                <select
                  className="yt-select"
                  value={monthCategoryFilter}
                  onChange={(e) => {
                    setMonthCategoryFilter(e.target.value);
                    setMonthSubCategoryFilter(FILTER_ALL);
                    setMonthColorFilter(FILTER_ALL);
                  }}
                >
                  <option value={FILTER_ALL}>All Categories</option>

                  {monthCategoryOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="yt-field">
                <label className="yt-label">Sub Category</label>

                <select
                  className="yt-select"
                  value={monthSubCategoryFilter}
                  onChange={(e) => {
                    setMonthSubCategoryFilter(e.target.value);
                    setMonthColorFilter(FILTER_ALL);
                  }}
                >
                  <option value={FILTER_ALL}>All Sub Categories</option>

                  {monthSubCategoryOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="yt-field">
                <label className="yt-label">Color</label>

                <select
                  className="yt-select"
                  value={monthColorFilter}
                  onChange={(e) => setMonthColorFilter(e.target.value)}
                >
                  <option value={FILTER_ALL}>All Colors</option>

                  {monthColorOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="yt-field">
                <label className="yt-label">Search Product</label>

                <input
                  className="yt-input"
                  value={monthSearch}
                  onChange={(e) => setMonthSearch(e.target.value)}
                  placeholder="Search product / color / HSN"
                />
              </div>

              <div className="yt-field">
                <label className="yt-label">Visible / Total</label>

                <div className="yt-stat">
                  <span>{monthRows.length}</span>
                  <b>{allMonthRows.length}</b>
                </div>
              </div>
            </div>

            <div className="yt-flowbar">
              <div className="yt-actions">
                <button
                  type="button"
                  className="yt-btn soft"
                  onClick={() => expandAllGroups(monthGroupedRows, "month")}
                >
                  Expand All
                </button>

                <button
                  type="button"
                  className="yt-btn soft"
                  onClick={() => collapseAllGroups(monthGroupedRows, "month")}
                >
                  Collapse All
                </button>
              </div>

              <div className="yt-status-note">
                Products are arranged category and sub-category wise.
              </div>
            </div>

            <div className="yt-modal-body">
              {renderProductTable({
                groups: monthGroupedRows,
                qtyMap: monthQtyMap,
                onQtyChange: setMonthSecondaryQty,
                expandedCategoryMap: monthExpandedCats,
                expandedSubMap: monthExpandedSubs,
                mode: "month",
                emptyText: "No product found for selected month filters.",
              })}
            </div>

            <div className="yt-modal-foot">
              <div className="yt-status-note">
                Saving this month applies the same target to this month and all
                upcoming editable months. Completed months remain unchanged.
              </div>

              <div className="yt-actions">
                <button
                  type="button"
                  className="yt-btn light"
                  onClick={() => {
                    setMonthEditor(null);
                    setMonthQtyMap({});
                    resetMonthFilters();
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="yt-btn primary"
                  onClick={saveMonthTarget}
                  disabled={savingMonth}
                >
                  {savingMonth ? "Saving..." : "Update Month Target"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
